import { registerLocaleData } from '@angular/common';
import { Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Translations } from '@app/core';
import { ErrorDetailsComponent } from '@app/shared/common';
import { PrimeNGCorrectionService } from '@app/shared/common/services/primeng-corrections.service';
import { TranslateService } from '@ngx-translate/core';
import { Message, PrimeNGConfig } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Toast } from 'primeng/toast';
import firebase from 'firebase/app';
import 'firebase/analytics';
import { environment } from '@environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  @ViewChild('appToast') appToast: Toast;


  langId: string;
  localeId: string;

  constructor(
    private primengConfig: PrimeNGConfig,
    public zone: NgZone,
    private dialogService: DialogService,
    private router: Router,
    public translate: TranslateService,
    public readonly lang: Translations,
    private primeNGCorrectionService: PrimeNGCorrectionService,
  ) {
    this.primeNGCorrectionService.init();

    this.translate.setDefaultLang('es');

    this.langId = 'es';
    this.localeId = 'es-AR';

    this.localeInitializer();
  }

  ngOnInit() {
    this.primengConfig.ripple = true;
    this.translate.get('COMMON.CALENDAR.MONTH_NAMES.month1').subscribe(() => {
      this.configurePrimeNGLocale();
    });

    // Initialize Firebase
    if (environment.firebaseConfig) {
      const app = firebase.initializeApp(environment.firebaseConfig);
      app.analytics();
    }
  }

  configurePrimeNGLocale() {
    this.primengConfig.setTranslation({
      dayNames: [
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES.day1),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES.day2),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES.day3),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES.day4),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES.day5),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES.day6),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES.day7),
      ],
      dayNamesShort: [
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_SHORT.day1),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_SHORT.day2),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_SHORT.day3),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_SHORT.day4),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_SHORT.day5),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_SHORT.day6),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_SHORT.day7),
      ],
      dayNamesMin: [
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_MIN.day1),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_MIN.day2),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_MIN.day3),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_MIN.day4),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_MIN.day5),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_MIN.day6),
        this.translate.instant(this.lang.COMMON.CALENDAR.DAY_NAMES_MIN.day7),
      ],
      monthNames: [
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month1),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month2),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month3),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month4),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month5),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month6),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month7),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month8),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month9),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month10),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month11),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES.month12),
      ],
      monthNamesShort: [
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month1),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month2),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month3),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month4),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month5),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month6),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month7),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month8),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month9),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month10),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month11),
        this.translate.instant(this.lang.COMMON.CALENDAR.MONTH_NAMES_SHORT.month12),
      ],
      today: this.translate.instant(this.lang.COMMON.CALENDAR.today),
      clear: this.translate.instant(this.lang.COMMON.CALENDAR.clear),
    });
  }

  localeInitializer() {
    this.translate.use(this.langId);

    return import(
      /* webpackInclude: /(es-AR)\.js$/ */
      `@angular/common/locales/${this.localeId}.js`
    ).then(module => registerLocaleData(module.default));
  }

  showErrorDetail(message: Message) {
    const messageIndex = this.appToast.messages.findIndex(item => item.id === message.id);
    this.appToast.onMessageClose({
      index: messageIndex,
      message
    });

    this.dialogService.open(ErrorDetailsComponent, {
      header: this.translate.instant(this.lang.COMMON.errorDetails),
      data: {
        message,
        path: this.router.url,
      },
      width: '70vw',
      baseZIndex: 1000010000,
    });
  }
}
